import React, { useEffect, useState, useContext } from 'react';

import type { SxProps } from '@mui/system';
import { ManageAvatar, SocieteGroupList } from '@europrocurement/l2d-modules';
import {
    CenterCircularProgress,
    EmptyBox,
    MenuItemProps,
    UserAccountDetails,
    UserMembersDetails,
    UserSecurityParameters,
    UserStructureDetails,
    NotificationsPreferences,
} from '@europrocurement/flexy-components';
import { useCurrentContact } from '@europrocurement/l2d-modules/hooks/useCurrentContact';
import { useSearchParams } from 'react-router-dom';
import { useCurrentPrescriber } from '@europrocurement/l2d-modules/hooks/useCurrentPrescriber';
import { ModalMenuContext } from '@europrocurement/flexy-components/hooks/useModalMenuContext';
import ThemePreferences from '@europrocurement/flexy-components/components/molecules/ThemePreferences/ThemePreferences';
import { DossierGroupList } from '../pages/Dossiers/components';

export type AvailableItems =
    | 'account_details'
    | 'themes'
    | 'notifications'
    | 'groups_dossiers'
    | 'groups_societes'
    | 'security'
    | 'user_structure'
    | 'members';

export type SetNewContentToDisplayProps = {
    contentToDisplayOnRender: AvailableItems;
};

type ContentState = 'loading' | 'ready' | 'no-content';

export const useUserPreferences = () => {
    const [contentDisplayed, setContentDisplayed] = useState<string | undefined>(undefined);
    const [contentState, setContentState] = useState<ContentState>('loading');
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const currContact = useCurrentContact();
    const currPrescriber = useCurrentPrescriber();
    const modalMenuContext = useContext(ModalMenuContext);

    /**
     * Change le contenu affiché avec une transition
     */
    const setNewContentToDisplay = async ({
        contentToDisplayOnRender,
    }: SetNewContentToDisplayProps) => {
        setIsTransitioning(true);

        // Petit délai pour permettre l'affichage du loader
        setTimeout(() => {
            setContentDisplayed(contentToDisplayOnRender);
            setIsTransitioning(false);
        }, 1000);
    };

    /**
     * Initialise le contenu affiché en fonction des paramètres URL
     */
    useEffect(() => {
        const settingsTarget = searchParams.get('settingsTarget') as AvailableItems | null;
        const targetContent = settingsTarget || 'account_details';

        setNewContentToDisplay({ contentToDisplayOnRender: targetContent });
        setContentState('ready');
    }, [searchParams, setSearchParams]);

    /**
     * Réinitialise activeTree à chaque changement de contenu pour éviter les conflits d'état
     * Uniquement si le contexte est disponible
     */
    useEffect(() => {
        if (modalMenuContext?.setActiveTree) {
            modalMenuContext.setActiveTree([]);
        }
    }, [contentDisplayed, modalMenuContext]);

    /**
     * Fonction helper pour créer les onClick avec l'état actif
     */
    const createMenuItemClick = (targetContent: AvailableItems) => () => {
        setNewContentToDisplay({ contentToDisplayOnRender: targetContent });
    };

    const customMenuItems: MenuItemProps[] = [
        {
            identifier: 'user_profil',
            label: 'Mon profil',
            header: true,
        },
        {
            identifier: 'account_details',
            label: 'Mon compte',
            onClick: createMenuItemClick('account_details'),
            icon: { icon: ['fasl', 'user-circle'], size: 'sm' },
            active: contentDisplayed === 'account_details',
        },
        {
            identifier: 'preferences',
            label: 'Mes préférences',
            icon: { icon: ['fasl', 'sliders-up'], size: 'sm' },
            childrenItems: [
                {
                    identifier: 'themes',
                    label: 'Thèmes',
                    onClick: createMenuItemClick('themes'),
                    icon: {
                        icon: ['fasl', 'paper-plane'],
                        sx: { display: 'none' },
                    },
                    active: contentDisplayed === 'themes',
                },
                {
                    identifier: 'notifications',
                    label: 'Notifications',
                    onClick: createMenuItemClick('notifications'),
                    icon: {
                        icon: ['fasl', 'paper-plane'],
                        sx: { display: 'none' },
                    },
                    active: contentDisplayed === 'notifications',
                },
                {
                    identifier: 'groups_dossiers',
                    label: 'Mes groupes de dossiers',
                    onClick: createMenuItemClick('groups_dossiers'),
                    icon: {
                        icon: ['fasl', 'paper-plane'],
                        sx: { display: 'none' },
                    },
                    active: contentDisplayed === 'groups_dossiers',
                },
                {
                    identifier: 'groups_societes',
                    label: 'Mes groupes de sociétés',
                    onClick: createMenuItemClick('groups_societes'),
                    icon: {
                        icon: ['fasl', 'paper-plane'],
                        sx: { display: 'none' },
                    },
                    active: contentDisplayed === 'groups_societes',
                },
            ],
        },
        {
            identifier: 'security',
            label: 'Sécurité',
            onClick: createMenuItemClick('security'),
            icon: { icon: ['fasl', 'shield'], size: 'sm' },
            hidden: true,
            active: contentDisplayed === 'security',
        },
        {
            identifier: 'society_parameters',
            label: 'Paramètres de la société',
            header: true,
        },
        {
            identifier: 'user_structure',
            label: 'Ma structure',
            onClick: createMenuItemClick('user_structure'),
            icon: { icon: ['fasl', 'building'], size: 'sm' },
            active: contentDisplayed === 'user_structure',
        },
        {
            identifier: 'members',
            label: 'Membres',
            onClick: createMenuItemClick('members'),
            icon: { icon: ['fasl', 'users'], size: 'sm' },
            hidden: true,
            active: contentDisplayed === 'members',
        },
    ];

    /**
     * Composant de chargement
     */
    const LoadingContent = function () {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1,
                    minHeight: '500px',
                    width: '100%',
                }}
            >
                <CenterCircularProgress />
            </div>
        );
    };

    const emptyBoxStyle: SxProps = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%',
    };

    /**
     * Rendu du contenu en fonction de l'état et de la sélection
     */
    const renderCurrentSelection = () => {
        if (contentState === 'loading' || isTransitioning) {
            return <LoadingContent />;
        }

        if (!contentDisplayed) {
            return (
                <EmptyBox
                    text="Aucun contenu disponible"
                    sx={emptyBoxStyle}
                />
            );
        }

        switch (contentDisplayed) {
            case 'account_details':
                return (
                    <UserAccountDetails
                        civilite={currContact?.civilite ?? undefined}
                        prenom={currContact?.prenomContact ?? undefined}
                        nom={currContact?.nomContact ?? undefined}
                        email={currContact?.emailsContact?.[0]?.email ?? undefined}
                        telephone={currContact?.telephonesContact?.[0]?.telephone ?? undefined}
                        manageAvatar={<ManageAvatar />}
                    />
                );
            case 'themes':
                return <ThemePreferences hideModeOptions />;
            case 'notifications':
                return <NotificationsPreferences />;
            case 'groups_dossiers':
                return <DossierGroupList />;
            case 'groups_societes':
                return <SocieteGroupList />;
            case 'security':
                return <UserSecurityParameters />;
            case 'user_structure':
                return <UserStructureDetails prescriber={currPrescriber} />;
            case 'members':
                return <UserMembersDetails />;
            default:
                return (
                    <EmptyBox
                        text="Aucun contenu disponible"
                        sx={emptyBoxStyle}
                    />
                );
        }
    };

    return {
        customMenuItems,
        setNewContentToDisplay,
        renderCurrentSelection,
        isLoading: contentState === 'loading' || isTransitioning,
        contentState,
        activeItem: contentDisplayed,
    };
};
